import React, { useState } from 'react';
import { useMeetings } from '../../hooks/useMeetings';
import { Edit, Trash2, X, Calendar, Clock, AlertCircle, Users, MessageSquare, FileText } from 'lucide-react';

interface MeetingActionsProps {
  meeting: any;
  onUpdate: () => void;
  onAttendanceClick?: () => void;
  onWhatsAppShare?: () => void;
  onPDFExport?: () => void;
}

export default function MeetingActions({ meeting, onUpdate, onAttendanceClick, onWhatsAppShare, onPDFExport }: MeetingActionsProps) {
  const { updateInstance, deleteInstance } = useMeetings();
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editForm, setEditForm] = useState({
    date: meeting.date,
    time: meeting.time || '',
    status: meeting.status
  });

  const handleEdit = async () => {
    setLoading(true);
    setError('');

    const result = await updateInstance(meeting.id, editForm);
    
    if (result.success) {
      setShowEditModal(false);
      onUpdate();
    } else {
      setError(result.error || 'Failed to update meeting');
    }
    
    setLoading(false);
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');

    const result = await deleteInstance(meeting.id);
    
    if (result.success) {
      setShowDeleteModal(false);
      onUpdate();
    } else {
      setError(result.error || 'Failed to delete meeting');
    }
    
    setLoading(false);
  };

  const handleStatusChange = (newStatus: string) => {
    setEditForm(prev => ({ ...prev, status: newStatus }));
  };

  return (
    <>
      <div className="flex items-center space-x-2">
        {onAttendanceClick && (
          <button
            onClick={onAttendanceClick}
            className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition"
            title="Manage Attendance"
          >
            <Users size={16} />
          </button>
        )}
        {onPDFExport && (
          <button
            onClick={onPDFExport}
            className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
            title="Export PDF Report"
          >
            <FileText size={16} />
          </button>
        )}
        {onWhatsAppShare && (
          <button
            onClick={onWhatsAppShare}
            className="p-1 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition"
            title="Share WhatsApp Summary"
          >
            <MessageSquare size={16} />
          </button>
        )}
        <button
          onClick={() => setShowEditModal(true)}
          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition"
          title="Edit Meeting"
        >
          <Edit size={16} />
        </button>
        <button
          onClick={() => setShowDeleteModal(true)}
          className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition"
          title="Delete Meeting"
        >
          <Trash2 size={16} />
        </button>
      </div>

      {/* Edit Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Edit Meeting</h2>
              <button
                onClick={() => setShowEditModal(false)}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-medium text-gray-900 mb-2">{meeting.meeting_types?.name}</h3>
                <p className="text-sm text-gray-500">Meeting: {meeting.meeting_types?.name}</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={editForm.status}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                  disabled={loading}
                >
                  <option value="Scheduled">Scheduled</option>
                  <option value="Completed">Completed</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Calendar size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="date"
                    value={editForm.date}
                    onChange={(e) => setEditForm(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    disabled={loading}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Time (Optional)
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Clock size={18} className="text-gray-400" />
                  </div>
                  <input
                    type="time"
                    value={editForm.time}
                    onChange={(e) => setEditForm(prev => ({ ...prev, time: e.target.value }))}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none"
                    disabled={loading}
                  />
                </div>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
                  {error}
                </div>
              )}
            </div>

            <div className="flex space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowEditModal(false)}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleEdit}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Delete Meeting</h2>
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={loading}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition disabled:opacity-50"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="p-3 bg-red-100 rounded-full">
                  <AlertCircle size={24} className="text-red-600" />
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">Are you sure?</h3>
                  <p className="text-sm text-gray-500">
                    This action cannot be undone. This will permanently delete the meeting and all associated attendance records.
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-medium text-gray-900">{meeting.meeting_types?.name}</h4>
                <p className="text-sm text-gray-600">{meeting.date} {meeting.time}</p>
                <p className="text-xs text-gray-500 mt-1">ID: {meeting.instance_id}</p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
                  {error}
                </div>
              )}
            </div>

            <div className="flex space-x-3 p-6 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={loading}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition disabled:opacity-50"
              >
                {loading ? 'Deleting...' : 'Delete Meeting'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
